import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { fromNow } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Gavel, CheckSquare, FileText, Receipt, Workflow, Info, ScrollText, Bell, BookOpen } from "lucide-react";

const CAT_ICON = {
  Hearings: Gavel, Tasks: CheckSquare, Orders: ScrollText, Documents: FileText,
  Billing: Receipt, Automation: Workflow, System: Info, Research: BookOpen,
};
const CATS = ["All", "Hearings", "Tasks", "Orders", "Documents", "Research", "Billing", "Automation", "System"];

export function NotificationsDrawer({ open, onOpenChange }) {
  const { db, markRead, markAllRead } = useApp();
  const navigate = useNavigate();
  const [cat, setCat] = useState("All");

  const items = db.notifications.filter((n) => cat === "All" || n.category === cat);

  const openMatter = (n) => {
    markRead(n.id);
    onOpenChange(false);
    if (n.draftId) navigate(`/drafting/${n.draftId}`);
    else if (n.matterId) navigate(`/matters/${n.matterId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[420px] p-0 flex flex-col" data-testid="notifications-drawer">
        <SheetHeader className="border-b border-line px-4 py-3">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-display text-xl flex items-center gap-2">
              <Bell className="h-5 w-5 text-copper" /> Notifications
            </SheetTitle>
            <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">
              Mark all read
            </Button>
          </div>
        </SheetHeader>
        <div className="flex flex-wrap gap-1.5 border-b border-line px-4 py-2">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={cn(
                "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
                cat === c ? "bg-copper text-white" : "bg-secondary text-ink-2 hover:bg-muted"
              )}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="nyk-scroll flex-1 overflow-y-auto">
          {items.length === 0 && (
            <div className="p-8 text-center text-sm text-ink-2">You're all caught up.</div>
          )}
          {items.map((n) => {
            const Icon = CAT_ICON[n.category] || Info;
            return (
              <button
                key={n.id}
                onClick={() => openMatter(n)}
                className={cn(
                  "flex w-full gap-3 border-b border-line px-4 py-3 text-left hover:bg-surface",
                  !n.read && "bg-copper-light/30"
                )}
              >
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary">
                  <Icon className="h-4 w-4 text-ink-2" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">{n.title}</span>
                    {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-copper" />}
                  </div>
                  <p className="mt-0.5 text-xs text-ink-2">{n.body}</p>
                  <span className="mt-1 block text-[11px] text-ink-muted">{fromNow(n.time)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
